import React, { PureComponent } from "react"
import classNames from "classnames"

import FounderWorkspaceChip from "../founder-workspace-chip/founder-workspace-chip"

import "./founder-hero.scss"

const DAY_MS = 24 * 60 * 60 * 1000

function greetingFor(hour) {
  if (hour < 5) return "Hello"
  if (hour < 12) return "Good morning"
  if (hour < 17) return "Good afternoon"
  if (hour < 22) return "Good evening"
  return "Hello"
}

function daysSince(iso) {
  if (!iso) return null
  const diff = Date.now() - new Date(iso).getTime()
  return Math.max(0, Math.floor(diff / DAY_MS))
}

function relativeDaysCopy(days) {
  if (days == null) return null
  if (days === 0) return "earlier today"
  if (days === 1) return "yesterday"
  if (days < 14) return `${days} days ago`
  if (days < 56) return `${Math.round(days / 7)} weeks ago`
  return `${Math.round(days / 30)} months ago`
}

function daysUntilQuarterlyWindow(fa) {
  if (!fa || !fa.lastQuarterlyAckAt) return null
  const ackMs = new Date(fa.lastQuarterlyAckAt).getTime()
  const dueMs = ackMs + 90 * DAY_MS
  const diff = dueMs - Date.now()
  return Math.ceil(diff / DAY_MS)
}

function buildSubtitle(fa) {
  if (!fa) {
    return "Tell us about your work and we'll draft a summary, a 6-month plan, and your first set of actions — in about 30 seconds."
  }
  if (!fa.questionnaireSavedAt) {
    // Empty workspace — distinguish brand-new users from users who just
    // switched to a fresh workspace.
    const wsLabel =
      fa.mode === "individual" ? "individual" : "company"
    return `Welcome to your ${wsLabel} workspace. Answer a few quick questions and we'll draft a plan tailored to it — in about 30 seconds.`
  }
  const parts = []
  const sinceCheckin = daysSince(fa.lastCheckinAt)
  if (sinceCheckin == null) {
    parts.push("You haven't done a check-in yet. Come back after a few days of progress.")
  } else if (sinceCheckin === 0) {
    parts.push("You checked in earlier today.")
  } else if (sinceCheckin === 1) {
    parts.push("Last check-in was yesterday.")
  } else {
    parts.push(`It's been ${sinceCheckin} days since your last check-in.`)
  }

  if (fa.needsQuarterlyReview) {
    parts.push("Your quarterly review window is open.")
  } else {
    const daysToQ = daysUntilQuarterlyWindow(fa)
    if (daysToQ != null && daysToQ <= 14 && daysToQ > 0) {
      parts.push(`Your 90-day mark is in ${daysToQ} days.`)
    }
  }
  return parts.join(" ")
}

// Light page top for FounderAssist (and `/` for members): the week's title
// and the workspace switch.
class FounderHero extends PureComponent {
  state = { today: null }

  componentDidMount() {
    // client only, so the server's timezone never shows
    this.setState({
      today: new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" }),
    })
  }

  render() {
    const { me, fa } = this.props
    const firstName = (me && me.firstName) || "founder"
    const hour = typeof window !== "undefined" ? new Date().getHours() : 10
    const subtitle = buildSubtitle(fa)
    const hasQuestionnaire = Boolean(fa && fa.questionnaireSavedAt)
    const hasPlan = Boolean(fa && (fa.summaryParagraph || fa.sixMonthPlanParagraph))
    // Show the chip whenever a workspace exists, even if the active workspace's
    // questionnaire is empty — the user needs a way to switch back to the
    // workspace they may have just left.
    const hasAnyWorkspace = Boolean(fa)
    const mode = (fa && fa.mode) || "company"
    const title = hasPlan
      ? `Your plan for this week, ${firstName}`
      : `${greetingFor(hour)}, ${firstName}. Let\u2019s write your first plan.`

    return (
      <header className={classNames("founder-hero", `founder-hero--${mode}`)}>
        <div className="founder-hero__inner">
          <div className="founder-hero__text">
            {this.state.today && <p className="founder-hero__date">{this.state.today}</p>}
            <h1 className="founder-hero__greeting">{title}</h1>
            {subtitle && <p className="founder-hero__subtitle">{subtitle}</p>}
          </div>
          <div className="founder-hero__side">
            {hasAnyWorkspace && <FounderWorkspaceChip fa={fa} />}
            {/* Check in lives in the progress ribbon just below */}
            {hasQuestionnaire && hasPlan && (
              <div className="founder-hero__actions">
                <a href="#refine" className="founder-hero__secondary">
                  Refine my answers
                </a>
              </div>
            )}
          </div>
        </div>
      </header>
    )
  }
}

export default FounderHero
